import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { AlertTriangle, Gem, Hammer, Lock, Minus, Package, Plus, Shield, Sword } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTutorial } from "@/components/game/TutorialProvider";
import { listItems } from "@/lib/game/items.functions";
import { getMyMonster } from "@/lib/game/monster.functions";
import { forgeUpgradeItem, projectForge } from "@/lib/game/forge.functions";
import {
  forgeCap,
  scaleStats as forgeScaleStats,
  computePower as forgeComputePower,
  costPerLevelStep,
  deriveBaseStats,
  FORGEABLE_KINDS,
  mapForgeError,
  type ItemStats,
} from "@/lib/game/forge";
import type { Rarity } from "@/lib/game/loot";
import { Panel } from "@/components/game/Panel";
import { RarityTag } from "@/components/game/Badges";
import { InfoTip } from "@/components/game/InfoTip";
import {
  GoldAmount,
  ItemIcon,
  MicroLabel,
  PageHeader,
  ScreenContainer,
} from "@/components/game/ScreenKit";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/game/schmiede")({
  component: SchmiedePage,
});

const STAT_LABEL: Record<string, string> = {
  atk: "ATK",
  def: "DEF",
  hp_max: "HP",
};

const STAT_ORDER = ["atk", "def", "hp_max"] as const;

/*
 * Bis zum 09.09.2026 stand hier ein ganzer Apparat, der vor `spd` warnte:
 * INERT_STATS, ein SPD_HINT und ein zweiter Power-Hinweis mit Warndreieck. Er
 * war nötig, solange die Schmiede einen Wert anzeigte, für den man bezahlt und
 * der im Kampf nichts bewirkt. Mit Stefans Entscheidung, Tempo überall zu
 * entfernen, ist er gegenstandslos — es gibt nichts mehr richtigzustellen.
 */

const POWER_HINT =
  "Power ist die Summe aller Item-Stats × 10 — dieselbe Formel, mit der das Spiel " +
  "Ausrüstung vergleicht.";

const LOCKED_HINT =
  "Gesperrte Teile bleiben in der Liste, lassen sich aber nicht schmieden — sie sind auf dem " +
  "Xeno-Basar gelistet, an eine andere Kreatur gebunden oder haben die Schmiedekappe deiner " +
  "Kreatur schon erreicht.";

const RARITIES = ["common", "rare", "epic", "legendary"] as const;

/**
 * `items.rarity` kommt als freier String aus der DB. Statt eines Casts wird der
 * Wert gegen die bekannten Raritäten geprüft — ein unbekannter Wert fällt auf
 * `common` zurück, statt Rahmen- und Kachelfarbe undefiniert zu lassen.
 */
function asRarity(value: string): Rarity {
  return RARITIES.find((r) => r === value) ?? "common";
}

/**
 * Platzhalter-Icons: echte Item-Grafiken gibt es nicht (siehe ScreenKit
 * `ItemIcon`). Die Rarität trägt ohnehin die Kachelfarbe, das Icon nur die Art.
 * Schmiedbar sind laut `FORGEABLE_KINDS` genau weapon / armor / accessory.
 */
const KIND_ICON = {
  weapon: Sword,
  armor: Shield,
  accessory: Gem,
} as const;

function kindIcon(kind: string) {
  if (kind === "weapon") return KIND_ICON.weapon;
  if (kind === "armor") return KIND_ICON.armor;
  if (kind === "accessory") return KIND_ICON.accessory;
  return Package;
}

type Item = {
  id: string;
  name: string;
  kind: string;
  rarity: string;
  level: number | null;
  power: number | null;
  stats: Record<string, unknown> | null;
  monster_id: string | null;
  equipped: boolean | null;
  marketplace_listing_id: string | null;
};

type ItemStatus = { code: string; label: string; blocked: boolean };

/**
 * Die aktive Kreatur, soweit diese Seite sie braucht. `gold` hängt
 * `attachWalletGold` serverseitig an (`profiles.gold`) — es ist derselbe
 * Wallet-Stand, den auch jeder andere Screen zeigt.
 */
type ActiveMonster = { id: string; level: number; gold?: number | null };

function extractBase(it: Item): ItemStats {
  const raw = (it.stats?.base ?? null) as Record<string, number> | null;
  if (raw && typeof raw === "object") {
    const out: ItemStats = {};
    for (const k of ["atk", "def", "spd", "hp_max"] as const) {
      const v = raw[k];
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    if (Object.keys(out).length > 0) return out;
  }
  return deriveBaseStats(
    (it.stats ?? {}) as Record<string, number>,
    asRarity(it.rarity),
    it.level ?? 1,
  );
}

/**
 * Eine Stat-Kachel der Vorschau: Ink-Fläche, Mikro-Label, alter Wert → neuer
 * Wert, darunter die Differenz. Grün heißt „mehr", rot „weniger" — die Zahl
 * selbst steht immer in tabellarischen Ziffern, damit die Kacheln in einer
 * Reihe nicht springen.
 *
 * `inert` kennzeichnet einen Stat, der im Zonenkampf nichts bewirkt: Dort
 * bleibt die Differenz absichtlich grau statt grün, und das Label trägt eine
 * Erklärblase. Ein stillschweigend grünes „+4" wäre genau die Sorte
 * Versprechen, die dieses Projekt sich schon zu oft geleistet hat.
 */
function StatTile({
  statKey,
  current,
  next,
  inert = false,
  hint,
}: {
  statKey: string;
  current: number | undefined;
  next: number | undefined;
  inert?: boolean;
  hint?: string;
}) {
  if (current == null && next == null) return null;
  const cur = current ?? 0;
  const nxt = next ?? 0;
  const diff = nxt - cur;
  const label = STAT_LABEL[statKey] ?? statKey;
  return (
    <div className="rounded-md border-2 border-ink bg-ink px-2 py-1.5 text-center">
      <MicroLabel>
        {inert && hint ? (
          <InfoTip text={hint}>
            <span className="inline-flex items-center gap-1">
              {label}
              <AlertTriangle className="h-3 w-3 shrink-0 text-primary" strokeWidth={2.5} />
            </span>
          </InfoTip>
        ) : (
          label
        )}
      </MicroLabel>
      <div className="mt-0.5 font-display text-[15px] leading-tight tabular-nums">
        <span className="text-muted-foreground">{cur}</span>
        <span className="text-muted-foreground"> → </span>
        <span className={diff > 0 && !inert ? "text-gold" : "text-foreground"}>{nxt}</span>
      </div>
      <div
        className={cn(
          "font-display text-[11px] leading-tight tabular-nums",
          inert
            ? "text-muted-foreground"
            : diff > 0
              ? "text-hp-ally"
              : diff < 0
                ? "text-destructive"
                : "text-muted-foreground",
        )}
      >
        {diff > 0 ? `+${diff}` : diff}
      </div>
    </div>
  );
}

/** Ganzflächiger Hinweis (Fehler / keine aktive Kreatur) statt einer halben Seite. */
function StateNote({ children }: { children: ReactNode }) {
  return (
    <Panel contentClassName="flex items-start justify-center gap-2 px-3 py-10">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" strokeWidth={2.5} />
      <span className="max-w-[46ch] text-[13px] text-muted-foreground">{children}</span>
    </Panel>
  );
}

function SchmiedePage() {
  const { t } = useTranslation("game");
  const { t: tCommon } = useTranslation("common");
  useTutorial("forge_intro");
  useTutorial("forge_coach");
  const qc = useQueryClient();
  const fetchItems = useServerFn(listItems);
  const fetchMonster = useServerFn(getMyMonster);
  const callForge = useServerFn(forgeUpgradeItem);

  const itemsQ = useQuery({ queryKey: ["items"], queryFn: () => fetchItems() });
  const monsterQ = useQuery({ queryKey: ["active-monster"], queryFn: () => fetchMonster() });

  // Gold kommt aus DERSELBEN Quelle wie überall sonst: `getMyMonster` hängt den
  // Wallet-Stand über `attachWalletGold` an die Kreatur (profiles.gold). Eine
  // zweite, direkte profiles-Abfrage daneben war ein Nachbau desselben Pfads —
  // und zeigte, solange sie lief, „0 Gold" an.
  const monster =
    (monsterQ.data as { monster?: ActiveMonster | null } | undefined)?.monster ?? null;

  // ⚠️ Kappe, Gold und Monster-ID sind ERST bekannt, wenn die Kreatur geladen
  // ist. Ein Fallback auf Level 1 ergäbe cap = 4 und damit lauter falsche
  // Behauptungen: falscher Untertitel, Schlösser an Items, die längst schmiedbar
  // sind, rote Kosten und ein toter Knopf. Deshalb `null` statt Platzhalter.
  const cap = monster ? forgeCap(monster.level) : null;
  const gold = monster ? Number(monster.gold ?? 0) : null;
  const monsterId = monster?.id ?? null;

  const allItems = ((itemsQ.data as { items?: Item[] } | undefined)?.items ?? []).filter((it) =>
    FORGEABLE_KINDS.has(it.kind),
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo(
    () => allItems.find((i) => i.id === selectedId) ?? null,
    [allItems, selectedId],
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [target, setTarget] = useState<number | null>(null);

  // Die Werkbank steht auf dem Handy UNTER der Liste — ohne diesen Anker
  // bliebe sie nach dem Antippen eines Items unter der Falz.
  const benchRef = useRef<HTMLDivElement | null>(null);

  // Sync target with selected item
  const effectiveTarget = useMemo(() => {
    if (!selected || cap == null) return null;
    const lvl = selected.level ?? 1;
    const min = lvl;
    const max = Math.max(lvl, cap);
    const t = target ?? Math.min(max, lvl + 1);
    return Math.max(min, Math.min(max, t));
  }, [selected, cap, target]);

  const onSelect = (it: Item) => {
    setSelectedId(it.id);
    setTarget(null);
    // Erst nach dem Rendern der Werkbank scrollen, sonst zielt der Sprung noch
    // auf die leere Karte. `block: "nearest"` bewegt breite Schirme nicht, dort
    // steht die Werkbank ohnehin daneben im Bild.
    requestAnimationFrame(() => {
      benchRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
  };

  const projection = useMemo(() => {
    if (!selected || effectiveTarget == null || cap == null) return null;
    const base = extractBase(selected);
    const rarity = asRarity(selected.rarity);
    const lvl = selected.level ?? 1;
    const currentStats = forgeScaleStats(base, rarity, lvl);
    const p = projectForge({ base, rarity, currentLevel: lvl, targetLevel: effectiveTarget });
    return {
      base,
      rarity,
      currentLevel: lvl,
      currentStats,
      currentPower: forgeComputePower(currentStats),
      ...p,
      nextStepCost: lvl < cap ? costPerLevelStep(lvl, rarity) : 0,
    };
  }, [selected, effectiveTarget, cap]);

  /**
   * Ohne geladene Kreatur sind nur „gelistet" und „andere Kreatur" ableitbar —
   * alles Weitere hängt an der Schmiedekappe. Solange die fehlt, wird gar keine
   * Liste gezeigt (siehe `loading` unten), also kann hier kein Schloss lügen.
   */
  const itemStatus = (it: Item): ItemStatus => {
    if (it.marketplace_listing_id)
      return { code: "listed", label: t("forge.status.listed"), blocked: true };
    if (it.monster_id && monsterId && it.monster_id !== monsterId)
      return { code: "other", label: t("forge.status.otherCreature"), blocked: true };
    if (cap == null) return { code: "unknown", label: "", blocked: false };
    const lvl = it.level ?? 1;
    if (lvl > cap) return { code: "over_cap", label: t("forge.status.overCap"), blocked: true };
    if (lvl >= cap) return { code: "at_cap", label: t("forge.status.atCap"), blocked: true };
    return { code: "ok", label: t("forge.status.forgeable"), blocked: false };
  };

  const mut = useMutation({
    mutationFn: async () => {
      if (!selected || effectiveTarget == null) return;
      await callForge({ data: { itemId: selected.id, targetLevel: effectiveTarget } });
    },
    onSuccess: () => {
      toast.success(t("forge.toastSuccess"));
      qc.invalidateQueries({ queryKey: ["items"] });
      // Gold hängt an der aktiven Kreatur (attachWalletGold) — ein eigener
      // Gold-Key existiert nicht mehr.
      qc.invalidateQueries({ queryKey: ["active-monster"] });
      qc.invalidateQueries({ queryKey: ["profile"] });
      setTarget(null);
    },
    onError: (e: Error) => {
      const code = mapForgeError(e.message);
      const key = `forge.errors.${code}` as const;
      toast.error(t(key, { defaultValue: e.message }));
    },
  });

  // Status EINMAL je Item bestimmen: Sortierung, Zählung und Karte lesen
  // denselben Wert — zwei Aufrufe daneben liefen sonst irgendwann auseinander.
  const rows = allItems
    .map((it) => ({ it, st: itemStatus(it) }))
    .sort((a, b) => {
      const sa = a.st.blocked ? 1 : 0;
      const sb = b.st.blocked ? 1 : 0;
      if (sa !== sb) return sa - sb;
      return (b.it.power ?? 0) - (a.it.power ?? 0);
    });
  const lockedCount = rows.filter((r) => r.st.blocked).length;
  const forgeableCount = rows.length - lockedCount;

  const bigJump =
    projection != null &&
    effectiveTarget != null &&
    (effectiveTarget - projection.currentLevel > 10 ||
      projection.totalCost > (gold ?? 0) * 0.25);

  const selectedStatus = selected ? itemStatus(selected) : null;
  const SelectedIcon = selected ? kindIcon(selected.kind) : Package;
  // Innerhalb dieses Zweigs ist das Ziel-Level immer gesetzt; der Fallback hält
  // TypeScript davon ab, hier eine Nicht-Null-Behauptung zu verlangen.
  const targetLevel = effectiveTarget ?? projection?.currentLevel ?? 1;

  // Erst wenn BEIDE Abfragen etwas geliefert haben, stehen Kappe und Bestand
  // fest. `isPending` ist in React Query v5 „noch keine Daten" — ein späteres
  // Nachladen (nach dem Schmieden) leert den Bildschirm also nicht.
  const loading = itemsQ.isPending || monsterQ.isPending;
  const failed = itemsQ.isError || monsterQ.isError;
  const noMonster = !loading && !failed && monster == null;

  // Gold zeigt die Seite nur, wenn sie es WEISS. Ist es noch unterwegs, steht
  // dort ein Platzhalter; steht die Seite im Fehlerzustand, gar nichts — eine
  // ausgedachte 0 wäre die falscheste aller Zahlen auf einem Kauf-Bildschirm.
  const goldCard =
    gold != null ? (
      <Panel tone="secondary" className="shadow-hard-sm" contentClassName="p-2.5">
        <MicroLabel>{t("forge.yourGold")}</MicroLabel>
        <GoldAmount value={gold} size="lg" className="mt-0.5" />
      </Panel>
    ) : failed || noMonster ? null : (
      <Panel tone="secondary" className="shadow-hard-sm" contentClassName="p-2.5">
        <MicroLabel>{t("forge.yourGold")}</MicroLabel>
        <Skeleton className="mt-1 h-4 w-16" />
      </Panel>
    );

  return (
    <ScreenContainer>
      <PageHeader
        title={t("forge.title")}
        icon={<Hammer className="h-6 w-6 shrink-0 text-primary" strokeWidth={2.5} />}
        // Der Untertitel nennt zwei konkrete Zahlen — den zeigt die Seite erst,
        // wenn sie beide wirklich kennt.
        sub={
          monster && cap != null
            ? t("forge.subtitle", { cap, monsterLevel: monster.level })
            : undefined
        }
        actions={goldCard ?? undefined}
      />

      {failed || noMonster ? (
        // Ein Lade-/Serverfehler ist KEINE leere Schmiede: Ohne diesen Zweig
        // stünde die Seite mit Kappe 4 da und behauptete, jedes Teil sei
        // gesperrt — ohne jeden Hinweis, dass nur etwas nicht geladen wurde.
        <StateNote>
          {failed ? tCommon("errors.generic") : t("forge.errors.NO_ACTIVE_MONSTER")}
        </StateNote>
      ) : (
        <div className="grid gap-4 md:grid-cols-[minmax(240px,320px)_1fr]">
          {/* Liste der schmiedbaren Items */}
          <div>
            {loading ? (
              <Skeleton className="mb-2 h-3 w-36" />
            ) : (
              <MicroLabel className="mb-2">
                <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                  {/* Gezählt werden nur die Teile, die sich WIRKLICH schmieden
                      lassen — gesperrte stehen daneben mit Schloss. */}
                  <span>{t("forge.forgeableCount", { count: forgeableCount })}</span>
                  {lockedCount > 0 && (
                    <InfoTip text={LOCKED_HINT}>
                      <span className="inline-flex items-center gap-1 text-primary">
                        <Lock className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                        <span className="tabular-nums">{lockedCount}</span>
                      </span>
                    </InfoTip>
                  )}
                </span>
              </MicroLabel>
            )}

            {/* Auf dem Handy steht die Werkbank UNTER der Liste: dort wird die
                Liste hart gekappt, damit nach dem Antippen sofort die Werkbank
                folgt (28 Teile sind am Ende einer Journey normal). Auf breiten
                Schirmen stehen beide nebeneinander, dort darf sie lang sein.
                `pb`/`pr` halten den harten Schatten und den Druck-Versatz der
                letzten Karte innerhalb des Scroll-Bereichs. */}
            <div className="grid max-h-[38vh] gap-2 overflow-y-auto pb-1.5 pr-1 md:max-h-[70vh]">
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[62px] w-full rounded-md" />
                ))}

              {!loading && rows.length === 0 && (
                <Panel contentClassName="p-5">
                  <p className="text-center text-[13px] text-muted-foreground">
                    {t("forge.emptyInventory")}
                  </p>
                </Panel>
              )}

              {!loading &&
                rows.map(({ it, st }) => {
                  const active = it.id === selectedId;
                  const rarity = asRarity(it.rarity);
                  const Icon = kindIcon(it.kind);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onSelect(it)}
                      aria-pressed={active}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md border-2 p-2 text-left",
                        "shadow-hard-sm transition-[transform,box-shadow,background-color,border-color] duration-100",
                        "active:translate-y-[4px] active:shadow-none",
                        active
                          ? "border-primary bg-primary/15"
                          : "border-ink bg-card hover:brightness-110",
                        st.blocked && !active && "opacity-70",
                      )}
                    >
                      <ItemIcon
                        icon={<Icon className="h-5 w-5" strokeWidth={2.5} />}
                        rarity={rarity}
                        size={40}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="line-clamp-2 text-[13px] font-extrabold leading-snug [overflow-wrap:anywhere]">
                          {it.name}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          <RarityTag rarity={rarity} />
                          <span className="text-[11px] font-extrabold tabular-nums text-muted-foreground">
                            Lv {it.level ?? 1}
                          </span>
                          {it.equipped && (
                            <span className="text-[11px] font-extrabold text-hp-ally">
                              {t("forge.equipped")}
                            </span>
                          )}
                          {st.blocked && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-primary">
                              <Lock className="h-3 w-3 shrink-0" strokeWidth={2.5} />
                              {st.label}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Werkbank */}
          <div ref={benchRef}>
            <Panel tone={selected ? "primary" : "default"} grid contentClassName="p-3.5">
              {loading && (
                <div className="space-y-3 py-2">
                  <Skeleton className="h-14 w-full rounded-md" />
                  <Skeleton className="h-10 w-full rounded-md" />
                  <Skeleton className="h-24 w-full rounded-md" />
                </div>
              )}

              {!loading && !selected && (
                <div className="flex flex-col items-center gap-3 py-14 text-center">
                  <div className="grid h-14 w-14 place-items-center rounded-md border-2 border-ink bg-secondary">
                    <Hammer className="h-6 w-6 text-muted-foreground" strokeWidth={2.5} />
                  </div>
                  <p className="max-w-[34ch] text-[13px] text-muted-foreground">
                    {t("forge.selectHint")}
                  </p>
                </div>
              )}

              {/* `cap` und `gold` stehen erst mit geladener Kreatur fest — die
                  beiden Null-Prüfungen sind daher keine Formalie, sondern das,
                  was falsche Zahlen aus der Werkbank heraushält. */}
              {!loading &&
                selected &&
                projection &&
                selectedStatus &&
                cap != null &&
                gold != null && (
                <div>
                  {/* Kopf: Item, Rarität, aktuelles Level und Kappe */}
                  <div className="flex items-start gap-3">
                    <ItemIcon
                      icon={<SelectedIcon className="h-7 w-7" strokeWidth={2.5} />}
                      rarity={projection.rarity}
                      size={56}
                    />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[19px] leading-tight [overflow-wrap:anywhere]">
                        {selected.name}
                      </h3>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <RarityTag rarity={projection.rarity} />
                        <span className="text-[11px] font-extrabold tabular-nums text-muted-foreground">
                          {t("forge.currentLv", { level: projection.currentLevel })} ·{" "}
                          {t("forge.limitLv", { cap })}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Power: Summe aller Item-Stats × 10 (computePower). */}
                  <div className="mt-3 flex items-center justify-between gap-3 rounded-md border-2 border-ink bg-ink px-3 py-2">
                    <MicroLabel>
                      <InfoTip text={POWER_HINT}>
                        <span className="inline-flex items-center gap-1">Power</span>
                      </InfoTip>
                    </MicroLabel>
                    <span className="font-display text-[17px] leading-none tabular-nums">
                      <span className="text-muted-foreground">{projection.currentPower}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span
                        className={
                          projection.projectedPower > projection.currentPower
                            ? "text-gold"
                            : "text-foreground"
                        }
                      >
                        {projection.projectedPower}
                      </span>
                    </span>
                  </div>

                  {selectedStatus.blocked ? (
                    <div className="mt-3.5 flex items-start gap-2 rounded-md border-2 border-ink bg-secondary p-3">
                      <AlertTriangle
                        className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                        strokeWidth={2.5}
                      />
                      <div className="text-[13px] leading-relaxed">
                        {selectedStatus.code === "over_cap" && t("forge.blocked.overCap")}
                        {selectedStatus.code === "at_cap" && t("forge.blocked.atCap")}
                        {selectedStatus.code === "listed" && t("forge.blocked.listed")}
                        {selectedStatus.code === "other" && t("forge.blocked.other")}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Ziel-Level. Der Slider allein wäre auf dem Handy ein
                          16-px-Ziel (Thumb `h-4`, geteilte Komponente) — die
                          beiden Stufen-Knöpfe sind 44 px hoch und die
                          eigentliche Bedienung unterwegs. Der Slider bekommt
                          zusätzlich vertikale Polsterung, damit auch seine
                          Trefferfläche 44 px hoch ist. */}
                      <div className="mt-3.5 rounded-md border-2 border-ink bg-secondary p-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <MicroLabel>{t("forge.targetLevel")}</MicroLabel>
                          <div className="flex items-center gap-1.5">
                            <Button
                              variant="secondary"
                              size="icon"
                              aria-label="Ziel-Level verringern"
                              disabled={targetLevel <= projection.currentLevel}
                              onClick={() =>
                                setTarget(Math.max(projection.currentLevel, targetLevel - 1))
                              }
                            >
                              <Minus strokeWidth={2.5} />
                            </Button>
                            <span className="min-w-[5ch] text-center font-display text-[19px] leading-none tabular-nums text-gold">
                              Lv {targetLevel}
                            </span>
                            <Button
                              variant="secondary"
                              size="icon"
                              aria-label="Ziel-Level erhöhen"
                              disabled={targetLevel >= cap}
                              onClick={() => setTarget(Math.min(cap, targetLevel + 1))}
                            >
                              <Plus strokeWidth={2.5} />
                            </Button>
                          </div>
                        </div>
                        <Slider
                          className="mt-1 py-3.5"
                          min={projection.currentLevel}
                          max={cap}
                          step={1}
                          value={[targetLevel]}
                          onValueChange={(v) => setTarget(v[0])}
                        />
                        <div className="flex items-center justify-between">
                          <MicroLabel>Lv {projection.currentLevel}</MicroLabel>
                          <MicroLabel>Lv {cap}</MicroLabel>
                        </div>
                      </div>

                      {/* Stat-Vorschau */}
                      <div className="mt-3.5">
                        <MicroLabel className="mb-2">{t("forge.statPreview")}</MicroLabel>
                        <div className="grid grid-cols-3 gap-2">
                          {STAT_ORDER.map((k) => (
                            <StatTile
                              key={k}
                              statKey={k}
                              current={projection.currentStats[k]}
                              next={projection.projectedStats[k]}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Kosten */}
                      <div className="mt-3.5 rounded-md border-2 border-ink bg-secondary p-3">
                        <div className="flex items-center justify-between gap-3 py-0.5">
                          <span className="text-[13px] text-muted-foreground">
                            {t("forge.nextStep")}
                          </span>
                          <GoldAmount value={projection.nextStepCost} size="md" />
                        </div>
                        <div className="flex items-center justify-between gap-3 py-0.5">
                          <span className="text-[13px] text-muted-foreground">
                            {t("forge.totalUpToLv", { level: targetLevel })}
                          </span>
                          <GoldAmount
                            value={projection.totalCost}
                            size="md"
                            className={cn(projection.totalCost > gold && "text-destructive")}
                          />
                        </div>
                        <div className="mt-1.5 flex items-center justify-between gap-3 border-t-2 border-ink pt-1.5">
                          <span className="text-[13px] font-extrabold">{t("forge.yourGold")}</span>
                          <GoldAmount value={gold} size="md" />
                        </div>
                      </div>

                      <Button
                        variant="gold"
                        className="mt-3.5 w-full"
                        data-tutorial-target="forge-upgrade"
                        disabled={
                          mut.isPending ||
                          targetLevel === projection.currentLevel ||
                          projection.totalCost > gold
                        }
                        onClick={() => (bigJump ? setConfirmOpen(true) : mut.mutate())}
                      >
                        <Hammer strokeWidth={2.5} />
                        {targetLevel === projection.currentLevel
                          ? t("forge.chooseTargetLevel")
                          : t("forge.forgeButton", {
                              cost: projection.totalCost.toLocaleString("de-DE"),
                            })}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("forge.confirmDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("forge.confirmDialog.description", {
                name: selected?.name,
                level: targetLevel,
                cost: projection?.totalCost.toLocaleString("de-DE"),
                pct: projection
                  ? Math.round((projection.totalCost / Math.max(1, gold ?? 0)) * 100)
                  : 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("forge.confirmDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                mut.mutate();
              }}
            >
              {t("forge.confirmDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScreenContainer>
  );
}
